import { LocalState } from '@apollo/client/local-state'

import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink
} from '@apollo/client'

import {
  ApolloProvider as DefaultApolloProvider,
  createQueryPreloader
} from '@apollo/client/react'

import { GraphQL, SharedAPI, utils } from '@guestgoodie/graphql'
import { getMainDefinition } from '@apollo/client/utilities'
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename'

import defaultUserAvatar from '@/assets/default_user_avatar.png'
import { apolloErrorLink } from '@/features/auth'

const IMG_PROXY_URL = import.meta.env.VITE_IMGPROXY_URL

const cache = new InMemoryCache({
  possibleTypes: {
    Goodie: ['Default', 'Event', 'Message'],
    Order: ['OrderDefault', 'OrderEvent']
  },
  typePolicies: {
    // Set up a base policy for all Goodie types
    Default: {
      keyFields: object => `Goodie:${object.id}`,
      fields: {
        hasConnectedDoor: {
          read(_, { readField }) {
            const integrationDoorId = readField<number | null>(
              'integrationDoorId'
            )
            return integrationDoorId != null
          }
        }
      }
    },
    Event: {
      keyFields: object => `Goodie:${object.id}`,
      fields: {
        hasConnectedDoor: {
          read(_, { readField }) {
            const integrationDoorId = readField<number | null>(
              'integrationDoorId'
            )
            return integrationDoorId != null
          }
        }
      }
    },
    Message: {
      keyFields: object => `Goodie:${object.id}`,
      fields: {
        hasConnectedDoor: {
          read(_, { readField }) {
            const integrationDoorId = readField<number | null>(
              'integrationDoorId'
            )
            return integrationDoorId != null
          }
        }
      }
    },
    Space: {
      fields: {
        hasConnectedDoor: {
          read(_, { readField }) {
            const integrationDoorId = readField<number | null>(
              'integrationDoorId'
            )
            return integrationDoorId != null
          }
        }
      }
    },
    Branding: { keyFields: [] },
    Billing: { keyFields: [] },
    IntegrationFeature: { keyFields: ['id'] },
    IntegrationConnector: { keyFields: ['provider'] },
    Organization: { keyFields: ['slug'] },
    File: {
      fields: {
        remoteUrl: {
          read(_, { readField }) {
            // Always recompute - never return persisted value
            const token = readField('token')
            const state = readField('state') as GraphQL.FileState
            const extension = readField('extension')
            const contentType = readField('contentType') as string

            if (!token || !extension || !contentType) return null

            if (contentType.startsWith('image/') && IMG_PROXY_URL) {
              return `${IMG_PROXY_URL}/_/preset:default/plain/${token}.${extension}`
            }

            if (state === GraphQL.FileState.Processing) {
              return `${import.meta.env.VITE_FILE_URL}/video_to_process/${token}.${extension}`
            }

            return `${import.meta.env.VITE_FILE_URL}/${token}.${extension}`
          }
        },
        remoteTinyUrl: {
          read(_, { readField }) {
            // Always recompute - never return persisted value
            const token = readField('token')
            const extension = readField('extension')
            const contentType = readField('contentType') as string

            if (!token || !extension || !contentType) return null

            if (contentType.startsWith('image/') && IMG_PROXY_URL) {
              return `${IMG_PROXY_URL}/_/preset:tiny/plain/${token}.${extension}`
            }

            return null
          }
        },
        remotePosterUrl: {
          read(_, { readField }) {
            // Always recompute - never return persisted value
            const token = readField('token')
            const posterExtension = readField('posterExtension')

            if (!token || !posterExtension) return null

            return `${import.meta.env.VITE_FILE_URL}/${token}.${posterExtension}`
          }
        },
        remoteTinyPosterUrl: {
          read(_, { readField }) {
            // Always recompute - never return persisted value
            const token = readField('token')
            const posterExtension = readField('posterExtension')

            if (!token || !posterExtension) return null

            if (IMG_PROXY_URL) {
              return `${IMG_PROXY_URL}/_/preset:tiny/plain/${token}.${posterExtension}`
            }

            return `${import.meta.env.VITE_FILE_URL}/${token}.${posterExtension}`
          }
        }
      }
    },
    User: {
      fields: {
        avatarUrl: {
          read(_, { readField }) {
            const hasAvatar = readField('hasAvatar') as boolean
            const avatarToken = readField('avatarToken') as string

            if (!hasAvatar || !avatarToken) return defaultUserAvatar

            return `${import.meta.env.VITE_FILE_URL}/avatars/${avatarToken}`
          }
        }
      }
    },
    Category: {
      fields: {
        categoryGoodies: {
          // We always update the whole array for the goodies of the category since they all needed for the positioning
          merge: false
        }
      }
    },
    FeedResult: { keyFields: ['version'] },
    Query: {
      fields: {
        getBranding: {
          keyArgs: false
        },
        querySpaces: {
          keyArgs: false,
          merge: utils.paginationHelpers.merge
        },
        queryStays: {
          keyArgs: false,
          merge: utils.paginationHelpers.merge
        },
        queryGuests: {
          keyArgs: false,
          merge: utils.paginationHelpers.merge
        },
        queryGoodies: {
          keyArgs: ['filter', ['search']],
          merge: utils.paginationHelpers.merge
        },
        queryPromotions: {
          keyArgs: false,
          merge: utils.paginationHelpers.merge
        },
        querySimpleFeedbacks: {
          merge: false
        },
        listRequestOrders: {
          merge: false
        },
        getLayouts: {
          merge: false
        },
        eventOccurencesBetween: {
          merge: false
        },
        // These are needed since we use variables, and Apollo likes to get confused in these cases with where to look
        getGoodie: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Goodie',
            fragment: SharedAPI.goodie.GOODIE_FRAGMENT,
            fragmentName: 'GoodieFragment'
          })
        },
        getPromotion: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Promotion',
            fragment: SharedAPI.promotion.PROMOTION_FRAGMENT,
            fragmentName: 'PromotionFragment'
          })
        },
        getStay: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Stay',
            fragment: SharedAPI.stay.STAY_FRAGMENT,
            fragmentName: 'StayFragment'
          })
        },
        getSpace: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Space',
            fragment: SharedAPI.space.SPACE_FRAGMENT,
            fragmentName: 'SpaceFragment'
          })
        },
        getGuest: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Guest',
            fragment: SharedAPI.guest.GUEST_FRAGMENT,
            fragmentName: 'GuestFragment'
          })
        },
        getEventOccurence: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'EventOccurence',
            fragment: SharedAPI.eventOccurence.EVENT_OCCURENCE_FRAGMENT,
            fragmentName: 'EventOccurenceFragment'
          })
        },
        getMembership: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'Membership',
            fragment: SharedAPI.membership.MEMBERSHIP_FRAGMENT,
            fragmentName: 'MembershipFragment'
          })
        },
        getMembershipInvite: {
          read: utils.cacheHelpers.toIdRead({
            typename: 'MembershipInvite',
            fragment: SharedAPI.membershipInvite.MEMBERSHIP_INVITE_FRAGMENT,
            fragmentName: 'MembershipInviteFragment'
          })
        },
        // Since a few our types are in the cache as TYPE:ID we need to manually search for them with the token
        getGoodieByToken: {
          read: utils.cacheHelpers.toTokenRead({
            typename: 'Goodie',
            fragment: SharedAPI.goodie.GOODIE_FRAGMENT,
            fragmentName: 'GoodieFragment'
          })
        },
        getPromotionByToken: {
          read: utils.cacheHelpers.toTokenRead({
            typename: 'Promotion',
            fragment: SharedAPI.promotion.PROMOTION_FRAGMENT,
            fragmentName: 'PromotionFragment'
          })
        },
        getStayByToken: {
          read: utils.cacheHelpers.toTokenRead({
            typename: 'Stay',
            fragment: SharedAPI.stay.STAY_FRAGMENT,
            fragmentName: 'StayFragment'
          })
        },
        getGuestByToken: {
          read: utils.cacheHelpers.toTokenRead({
            typename: 'Guest',
            fragment: SharedAPI.guest.GUEST_FRAGMENT,
            fragmentName: 'GuestFragment'
          })
        },
        getEventOccurenceByToken: {
          read: utils.cacheHelpers.toTokenRead({
            typename: 'EventOccurence',
            fragment: SharedAPI.eventOccurence.EVENT_OCCURENCE_FRAGMENT,
            fragmentName: 'EventOccurenceFragment'
          })
        }
      }
    }
  }
})

const localState = new LocalState({
  resolvers: {
    User: {
      avatarUrl: (
        user: Pick<GraphQL.User, 'hasAvatar' | 'avatarToken'>
      ): string => {
        if (!user.hasAvatar || !user.avatarToken) return defaultUserAvatar
        return `${import.meta.env.VITE_FILE_URL}/avatars/${user.avatarToken}`
      }
    },
    Default: {
      hasConnectedDoor: (
        goodie: Pick<GraphQL.Default, 'integrationDoorId'>
      ): boolean => {
        return goodie.integrationDoorId != null
      }
    },
    Event: {
      hasConnectedDoor: (
        goodie: Pick<GraphQL.Event, 'integrationDoorId'>
      ): boolean => {
        return goodie.integrationDoorId != null
      }
    },
    Message: {
      hasConnectedDoor: (
        goodie: Pick<GraphQL.Message, 'integrationDoorId'>
      ): boolean => {
        return goodie.integrationDoorId != null
      }
    },
    Space: {
      hasConnectedDoor: (
        space: Pick<GraphQL.Space, 'integrationDoorId'>
      ): boolean => {
        return space.integrationDoorId != null
      }
    }
  }
})

const API_URL = import.meta.env.VITE_API_URL
const ADMIN_URL = `${API_URL}/admin/graphql`

const removeTypenameLink = new RemoveTypenameFromVariablesLink()
const csfrLink = utils.csrfMiddleware({ url: API_URL })

const httpLink = new HttpLink({
  uri: operation => {
    if (operation.operationName) {
      return `${ADMIN_URL}?o=${encodeURIComponent(operation.operationName)}`
    }

    return ADMIN_URL
  },
  credentials: 'include'
})

const sseLink = new utils.SSELink({
  url: ADMIN_URL,
  credentials: 'include'
})

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)

    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  sseLink,
  httpLink
)

export const client = new ApolloClient({
  link: ApolloLink.from([
    removeTypenameLink,
    apolloErrorLink,
    csfrLink,
    splitLink
  ]),

  cache,

  clientAwareness: {
    name: 'GuestGoodieAdminApp',
    version: '1.0.0'
  },

  localState
})

const ApolloProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <DefaultApolloProvider client={client}>{children}</DefaultApolloProvider>
  )
}

export const preloadQuery = createQueryPreloader(client)

export default ApolloProvider
